import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import SectionTabs from '../../section-tabs/section-tabs';
import LibraryTranslationsTable from './components/LibraryTranslationsTable';
import LibraryUploadModal from './components/LibraryUploadModal';
import LibraryDeployProDialog from './components/LibraryDeployProDialog';
import LibraryDeleteFileDialog from './components/LibraryDeleteFileDialog';
import { FilterControl, FilterSelect } from '../../table/filter-controls';
import { getCountryCode } from '../model/library-mapper';
import './LibraryView.scss';

const REPORTS_TAB_ENABLED = false;

const LibraryView = ({
  activeTab,
  onTabChange,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  countryFilter,
  onCountryFilterChange,
  countryFilterOptions,
  typeFilter,
  onTypeFilterChange,
  rows,
  isLoading,
  onOpenAddModal,
  onDeployPre,
  onDeployPro,
  onDownload,
  onPreviewAs,
  onDelete,
  successMessage,
  errorMessage,
  onCloseSuccessMessage,
  onCloseErrorMessage,
  isAddModalOpen,
  onCloseAddModal,
  form,
  countryOptions,
  languageOptions,
  typeOptions,
  onUpdateForm,
  onUpload,
  isSubmitting,
  isUploadDisabled,
  formError,
  pendingProFile,
  isPublishingPro,
  deployProError,
  onClearDeployProError,
  onCancelDeployPro,
  onConfirmDeployPro,
  pendingDeleteFile,
  onCancelDelete,
  onConfirmDelete,
  canPublishPre,
  canSeePublishPro,
  canPublishPro,
  canUploadFile,
  canDeleteFile,
  canViewReports,
  canUploadMasterEnglish,
}) => {
  const { t } = useTranslation();

  const tabs = [
    ...(REPORTS_TAB_ENABLED && canViewReports
      ? [{ id: 'reports', label: t('library-tab-reports') }]
      : []),
    { id: 'translations', label: t('library-tab-translations') },
  ];

  return (
    <section className="library-view">
      <h2>{t('library-title')}</h2>

      <div className="library-view__header">
        <SectionTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabClick={onTabChange}
          className="library-view__tabs"
        />
        {activeTab === 'translations' && canUploadFile ? (
          <button type="button" className="btn library-view__add-btn" onClick={onOpenAddModal}>
            {t('library-button-add-new-file')}
          </button>
        ) : null}
      </div>

      {activeTab === 'translations' ? (
        <div className="library-view__toolbar">
          <div className="form-control">
            <label htmlFor="library-search">{t('library-label-search-document')}</label>
            <input
              id="library-search"
              type="text"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </div>
          <FilterControl id="library-country-filter" label={t('library-column-country')}>
            <FilterSelect
              id="library-country-filter"
              name="country"
              value={countryFilter}
              onChange={(event) => onCountryFilterChange(event.target.value)}
              options={countryFilterOptions.map((opt) => {
                const code = getCountryCode(opt.value);
                return {
                  value: opt.value,
                  label: code
                    ? t(`countries-${code}`, { defaultValue: opt.value })
                    : opt.label,
                };
              })}
              allLabel={t('common-all-country')}
            />
          </FilterControl>
          <FilterControl id="library-status-filter" label={t('library-column-status')}>
            <FilterSelect
              id="library-status-filter"
              name="status"
              value={statusFilter}
              onChange={(event) => onStatusFilterChange(event.target.value)}
              options={[
                { value: 'pending', label: t('library-status-pending') },
                { value: 'pre', label: t('library-status-pre') },
                { value: 'pro', label: t('library-status-pro') },
              ]}
              allLabel={t('common-all-status')}
            />
          </FilterControl>
          <FilterControl id="library-type-filter" label={t('library-column-type')}>
            <FilterSelect
              id="library-type-filter"
              name="type"
              value={typeFilter}
              onChange={(event) => onTypeFilterChange(event.target.value)}
              options={typeOptions.map((type) => ({ value: type.value, label: t(type.labelKey) }))}
              allLabel={t('common-all-types')}
            />
          </FilterControl>
        </div>
      ) : null}

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={onCloseSuccessMessage}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={3000}
        onClose={onCloseErrorMessage}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {REPORTS_TAB_ENABLED && activeTab === 'reports' ? (
        <div className="library-view__empty-state">
          {t('library-reports-empty')}
        </div>
      ) : null}

      {activeTab === 'translations' ? (
        <>
          {isLoading ? (
            <div className="library-view__loading">{t('library-loading-files')}</div>
          ) : rows.length ? (
            <LibraryTranslationsTable
              rows={rows}
              onDeployPre={onDeployPre}
              onDeployPro={onDeployPro}
              onDownload={onDownload}
              onDelete={onDelete}
              canPublishPre={canPublishPre}
              canSeePublishPro={canSeePublishPro}
              canPublishPro={canPublishPro}
              canDeleteFile={canDeleteFile}
              onPreviewAs={onPreviewAs}
              t={t}
            />
          ) : (
            <div className="library-view__empty-state">{t('library-empty-files')}</div>
          )}
        </>
      ) : null}

      {canUploadFile ? (
        <LibraryUploadModal
          isOpen={isAddModalOpen}
          onClose={onCloseAddModal}
          form={form}
          countryOptions={countryOptions}
          languageOptions={languageOptions}
          typeOptions={typeOptions}
          onUpdate={onUpdateForm}
          onSubmit={onUpload}
          isSubmitting={isSubmitting}
          isUploadDisabled={isUploadDisabled}
          error={formError}
          canUploadMasterEnglish={canUploadMasterEnglish}
          t={t}
        />
      ) : null}

      {canPublishPro ? (
        <LibraryDeployProDialog
          isOpen={Boolean(pendingProFile)}
          fileName={pendingProFile?.fileName || ''}
          onCancel={onCancelDeployPro}
          onConfirm={onConfirmDeployPro}
          onClearError={onClearDeployProError}
          isSubmitting={isPublishingPro}
          error={deployProError}
          t={t}
        />
      ) : null}

      {canDeleteFile ? (
        <LibraryDeleteFileDialog
          isOpen={Boolean(pendingDeleteFile)}
          fileName={pendingDeleteFile?.fileName || ''}
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
          t={t}
        />
      ) : null}
    </section>
  );
};

export default LibraryView;
